import React, { Component } from 'react'
import get from 'lodash/get'
import ApplicationChoice from '../components/application/applicationChoice'
import ApplicationRequirements from '../components/application/applicationRequirements'
import ApplicationKnowledge from '../components/application/applicationKnowledge'
import ApplicationProcedure from '../components/application/applicationProcedure'

export default class ApplicationPage extends Component {
  constructor(props) {
    super(props)
    const initialState = {
      height: getWindowHeight(),
      step: 1,
      choice: undefined,
    }
    let oldState
    if (typeof window !== 'undefined') {
      oldState = JSON.parse(
        window.sessionStorage.getItem('applicationState') || '{}'
      )
      oldState.height = getWindowHeight()
    }
    this.state =
      typeof oldState !== 'undefined' &&
      oldState.hasOwnProperty('step') &&
      oldState.hasOwnProperty('choice')
        ? oldState
        : initialState
  }

  componentDidUpdate(_, prevState) {
    if (this.state.step !== prevState.step) {
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0)
      }
    }
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(
        'applicationState',
        JSON.stringify(this.state)
      )
    }
  }

  componentDidMount() {
    if (window) {
      window.addEventListener('resize', this.setHeight)
    }
  }

  componentWillUnmount() {
    if (window) {
      window.removeEventListener('resize', this.setHeight)
    }
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(
        'applicationState',
        JSON.stringify(this.state)
      )
    }
  }

  render() {
    const { height, step } = this.state
    const isChoiceStep = step === 1
    const isRequirementStep = step === 2
    const isKnowledgeStep = step === 3
    const isProcedureStep = step === 4

    return (
      <div
        style={{
          fontFamily: "'Libre Franklin', 'sans serif'",
        }}
      >
        {isChoiceStep && (
          <ApplicationChoice
            height={height}
            setChoice={this.setChoice}
            {...this.getApplicationChoiceData()}
          />
        )}
        {isRequirementStep && (
          <ApplicationRequirements
            height={height}
            nextStep={this.nextStep}
            previousStep={this.previousStep}
            {...this.getApplicationRequirementData()}
          />
        )}
        {isKnowledgeStep && (
          <ApplicationKnowledge
            height={height}
            nextStep={this.nextStep}
            previousStep={this.previousStep}
            {...this.getApplicationKnowledgeData()}
          />
        )}
        {isProcedureStep && (
          <ApplicationProcedure
            height={height}
            nextStep={this.nextStep}
            previousStep={this.previousStep}
            {...this.getApplicationProcedureData()}
          />
        )}
      </div>
    )
  }

  nextStep = () => this.setState(({ step }) => ({ step: step + 1 }))
  previousStep = () => this.setState(({ step }) => ({ step: step - 1 }))
  setChoice = choice =>
    this.setState(({ step }) => ({ choice, step: step + 1 }))
  setHeight = (height = getWindowHeight()) => this.setState({ height })

  getApplicationChoiceData = () => {
    const [{ node: choiceData }] = get(
      this,
      'props.data.allContentfulApplicationChoice.edges'
    )
    const [{ node: programmeData }] = get(
      this,
      'props.data.allContentfulProgrammePage.edges'
    )
    const {
      header: {
        title,
        titleAccent,
        text: {
          internal: { content: text },
        },
      },
      tracks,
      step: { text: stepText },
      explanationDegrees: {
        internal: { content: explanationDegrees },
      },
      doubleDegree: doubleDegreeTitle,
      doubleDegreeText: {
        internal: { content: doubleDegreeText },
      },
    } = { ...choiceData, ...programmeData }
    return {
      title,
      titleAccent,
      text,
      tracks,
      stepText,
      explanationDegrees,
      doubleDegreeTitle,
      doubleDegreeText,
    }
  }

  getApplicationKnowledgeData = () => {
    const [{ node: knowledgeData }] = get(
      this,
      'props.data.allContentfulApplicationKnowledge.edges'
    ).filter(({ node }) => node.track.trackName === this.state.choice)
    return knowledgeData
  }

  getApplicationRequirementData = () => {
    const [{ node: requirementData }] = get(
      this,
      'props.data.allContentfulApplicationRequirements.edges'
    ).filter(({ node }) => node.track.trackName === this.state.choice)
    return requirementData
  }

  getApplicationProcedureData = () => {
    const [{ node: procedureData }] = get(
      this,
      'props.data.allContentfulApplicationProcedure.edges'
    ).filter(({ node }) => node.track.trackName === this.state.choice)
    return procedureData
  }
}

const getWindowHeight = () => {
  if (typeof window !== 'undefined' && window.hasOwnProperty('innerHeight')) {
    return window.innerHeight - getHeaderHeight() > 600
      ? window.innerHeight - getHeaderHeight()
      : 600
  }
  return 600
}
const getHeaderHeight = () => {
  if (typeof window !== 'undefined') {
    if (window.innerWidth < 768) {
      return 3.5 * 18
    } else if (window.innerWidth < 992) {
      return 3 * 18
    } else {
      return 2.5 * 18
    }
  }
}

export const applicationQuery = graphql`
  query application {
    allContentfulApplicationChoice {
      edges {
        node {
          header {
            title
            titleAccent
            text {
              internal {
                content
              }
            }
          }
          step {
            current
            max
            text
          }
          tracks {
            trackName
          }
          explanationDegrees {
            internal {
              content
            }
          }
        }
      }
    }
    allContentfulApplicationKnowledge {
      edges {
        node {
          track {
            trackName
          }
          header {
            title
            titleAccent
            text {
              internal {
                content
              }
            }
          }
          step {
            current
            max
            text
            accent
          }
          legalisations
          legalisationsText {
            internal {
              content
            }
          }
          tuitionAndScholarships
          tuitionAndScholarshipsText {
            internal {
              content
            }
          }
          contact
          contactText {
            internal {
              content
            }
          }
          applicationProcedure
          applicationProcedureText {
            internal {
              content
            }
          }
          closingText
        }
      }
    }
    allContentfulApplicationProcedure {
      edges {
        node {
          track {
            trackName
          }
          header {
            title
            titleAccent
            text {
              internal {
                content
              }
            }
          }
          step {
            current
            max
            text
          }
          eConsortLink {
            url
            displayName
          }
          registrationText
          registeredButtonText
          applicationFormLink {
            url
            displayName
          }
          applicationText
        }
      }
    }
    allContentfulApplicationRequirements {
      edges {
        node {
          track {
            trackName
          }
          header {
            title
            titleAccent
            text {
              internal {
                content
              }
            }
          }
          step {
            current
            max
            text
          }
          requirements {
            title
            text {
              internal {
                content
              }
            }
          }
          closingText
        }
      }
    }
    allContentfulProgrammePage {
      edges {
        node {
          doubleDegree
          doubleDegreeText {
            internal {
              content
            }
          }
        }
      }
    }
  }
`



// WEBPACK FOOTER //
// ./src/pages/application.js